import type { GoogleIdentityServices } from './types';

const SCRIPT_ID = 'google-identity-services';
const SCRIPT_URL = 'https://accounts.google.com/gsi/client';

let scriptPromise: Promise<GoogleIdentityServices> | undefined;

const getGoogleIdentityServices = (): GoogleIdentityServices | undefined =>
  window.google?.accounts?.id;

export function loadGoogleIdentityServices(): Promise<GoogleIdentityServices> {
  const services = getGoogleIdentityServices();
  if (services) return Promise.resolve(services);

  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<GoogleIdentityServices>((resolve, reject) => {
    const existingScript = document.getElementById(SCRIPT_ID);
    const script =
      existingScript instanceof HTMLScriptElement
        ? existingScript
        : document.createElement('script');

    const resolveServices = (): void => {
      const loadedServices = getGoogleIdentityServices();
      if (loadedServices) {
        resolve(loadedServices);
      } else {
        reject(new Error('Google Identity Services loaded without its API'));
      }
    };

    script.addEventListener('load', resolveServices, { once: true });
    script.addEventListener(
      'error',
      () => {
        if (!existingScript) script.remove();
        reject(new Error('Unable to load Google Identity Services'));
      },
      { once: true }
    );

    if (!existingScript) {
      script.id = SCRIPT_ID;
      script.src = SCRIPT_URL;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }).catch((error: unknown) => {
    scriptPromise = undefined;
    throw error;
  });

  return scriptPromise;
}

export function resetGoogleIdentityServicesLoader(): void {
  scriptPromise = undefined;
}
